import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { DriveType } from "../../../../types/drive";
import { Drive } from "../../../../types/drive";
import { formatNumber, formatCurrency } from "../../../../utils/formatting";
import { addDrive, removeDrive, updateDriveRole } from "../../../../store/features/shipBuilderSlice";
import { getDriveTons, getDriveCost } from "../../../../constants/drives";

interface MultipleDriveManagerProps {
  driveType: DriveType;
}

export const MultipleDriveManager: React.FC<MultipleDriveManagerProps> = ({ driveType }) => {
  const dispatch = useAppDispatch();
  const drives = useAppSelector(selectors.selectDrives) || [];
  const shipSize = useAppSelector(selectors.selectShipSize);
  const techLevel = useAppSelector((state) => state.shipBuilder.techLevel) as number;
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);
  

  // Get drives of this type
  const drivesOfType = drives.filter((d) => d.type === driveType);
  const primaryDrive = drivesOfType.find((d) => d.role === 'primary');
  const additionalDrives = drivesOfType.filter((d) => d.role === 'additional');

  // Get drive options based on tech level
  const getDriveOptions = (): Array<{ rating: number; label: string; tons: number; cost: number; techLevel: number }> => {
    const options = [];
    const maxRating = driveType === DriveType.JUMP ? 9 : 16;
    
    // Add '0' rating for Maneuver and Reaction drives (not for Jump drives)
    if (driveType === DriveType.MANEUVER || driveType === DriveType.REACTION) {
      const tons0 = driveType === DriveType.MANEUVER ? (0.5 / 100) * shipSize : (1 / 100) * shipSize;
      const cost0 = driveType === DriveType.MANEUVER ? tons0 * 2.0 : tons0 * 0.2;
      
      // Get correct TL for rating 0
      const techLevel0 = driveType === DriveType.MANEUVER ? 9 : 7; // Maneuver 0 = TL 9, Reaction 0 = TL 7
      
      options.push({
        rating: 0,
        label: 'Thrust 0',
        tons: tons0,
        cost: cost0 * 1000000, // Convert to credits
        techLevel: techLevel0,
      });
    }
    
    // Add ratings 1 through maxRating
    for (let i = 1; i <= maxRating; i++) {
      // Calculate tons and cost based on drive type and rating
      const tons = driveType === DriveType.MANEUVER ? (i / 100) * shipSize :
                   driveType === DriveType.JUMP ? (i * 2.5 / 100) * shipSize :
                   (i * 2 / 100) * shipSize;
      
      const cost = driveType === DriveType.MANEUVER ? tons * 2.0 :
                   driveType === DriveType.JUMP ? tons * 1.5 :
                   tons * 0.2;
      
      // Get correct TL based on Traveller rules
      let techLevelForRating = 7; // Default fallback
      
      if (driveType === DriveType.MANEUVER) {
        // Maneuver Drive TLs from Traveller rules
        if (i <= 1) techLevelForRating = 9;
        else if (i <= 3) techLevelForRating = 10;
        else if (i <= 5) techLevelForRating = 11;
        else if (i === 6) techLevelForRating = 12;
        else if (i === 7) techLevelForRating = 13;
        else if (i === 8) techLevelForRating = 14;
        else if (i === 9) techLevelForRating = 15;
        else if (i === 10) techLevelForRating = 16;
        else if (i === 11) techLevelForRating = 17;
        else if (i >= 12 && i <= 16) techLevelForRating = 0; // Not specified in rules, will be filtered out
      } else if (driveType === DriveType.REACTION) {
        // Reaction Drive TLs from Traveller rules
        if (i <= 3) techLevelForRating = 7;
        else if (i <= 6) techLevelForRating = 8;
        else if (i <= 9) techLevelForRating = 9;
        else if (i <= 12) techLevelForRating = 10;
        else if (i <= 15) techLevelForRating = 11;
        else if (i === 16) techLevelForRating = 12;
      } else if (driveType === DriveType.JUMP) {
        // Jump Drive TLs from Traveller rules
        if (i === 1) techLevelForRating = 9;
        else if (i === 2) techLevelForRating = 11;
        else if (i === 3) techLevelForRating = 12;
        else if (i === 4) techLevelForRating = 13;
        else if (i === 5) techLevelForRating = 14;
        else if (i === 6) techLevelForRating = 15;
        else if (i === 7) techLevelForRating = 16;
        else if (i === 8) techLevelForRating = 17;
        else if (i === 9) techLevelForRating = 18;
      }
      
      // Only add option if it has a valid TL (filter out unsupported ratings)
      if (techLevelForRating > 0) {
        options.push({
          rating: i,
          label: driveType === DriveType.JUMP ? `Jump ${i}` : `Thrust ${i}`,
          tons,
          cost: cost * 1000000, // Convert to credits
          techLevel: techLevelForRating,
        });
      }
    }
    
    return options.filter(option => tlOverrideActive || option.techLevel <= techLevel);
  };

  const driveOptions = getDriveOptions();

  // Debug: Log drives changes
  useEffect(() => {
    console.log(`${driveType} drives updated:`, drivesOfType);
  }, [drivesOfType, driveType]);


  const handleRemoveDrive = (driveId: string) => {
    console.log(`Removing drive with ID: ${driveId}`);
    console.log(`Current drives before removal:`, drivesOfType);
    dispatch(removeDrive({ driveId }));
  };

  const handleUpdateDriveRole = (driveId: string, role: 'primary' | 'additional') => {
    dispatch(updateDriveRole({ driveId, role }));
  };

  const handleSetPrimaryDrive = (type: DriveType, rating: number) => {
    // First, remove any existing primary drive of this type
    const existingPrimary = drivesOfType.find((d) => d.role === 'primary');
    if (existingPrimary && existingPrimary.id) {
      dispatch(removeDrive({ driveId: existingPrimary.id }));
    }
    
    // Then add the new primary drive
    dispatch(addDrive({ type, rating, role: 'primary' }));
  };

  const getDriveTypeName = () => {
    switch (driveType) {
      case DriveType.MANEUVER:
        return "Maneuver Drive";
      case DriveType.JUMP:
        return "Jump Drive";
      case DriveType.REACTION:
        return "Reaction Drive";
      default:
        return "Drive";
    }
  };

  // Helper function for drive type short names (currently unused)
  // const getDriveTypeShortName = () => { ... }

  // Use centralized calculation functions for consistency with footer
  const calculateDriveTonnage = (rating: number): number => {
    return getDriveTons(driveType, rating, shipSize);
  };

  const calculateDriveCost = (rating: number): number => {
    return getDriveCost(driveType, rating, shipSize);
  };

  const renderDriveCard = (drive: Drive, index: number) => {
    const tonnage = calculateDriveTonnage(drive.rating);
    const cost = calculateDriveCost(drive.rating);
    
    // Ensure drive has an ID (fallback for legacy drives)
    const driveId = drive.id || `${drive.type}-${drive.role || 'additional'}-${index}-${Date.now()}`;
    
    return (
      <div key={drive.id || index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-3">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  drive.role === 'primary' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {drive.role?.toUpperCase() || 'ADDITIONAL'}
                </span>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {getDriveTypeName()} {drive.rating > 0 ? drive.rating : '0'}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatNumber(tonnage)} tons • {formatCurrency(cost)}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={drive.role || 'additional'}
              onChange={(e) => handleUpdateDriveRole(driveId, e.target.value as 'primary' | 'additional')}
              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="primary">Primary</option>
              <option value="additional">Additional</option>
            </select>
            <button
              onClick={() => handleRemoveDrive(driveId)}
              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {getDriveTypeName()} Management
        </h3>
      </div>

      {/* Primary Drive Selection */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Primary {getDriveTypeName()}
          </label>
          <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">
            Primary Drive choices automatically adjust Power Plant size and Fuel Tank Capacities
          </span>
        </div>
        <select
          value={primaryDrive ? primaryDrive.rating.toString() : "none"}
          onChange={(e) => {
            const rating = e.target.value;
            if (rating === "none") {
              // Remove primary drive
              if (primaryDrive?.id) {
                handleRemoveDrive(primaryDrive.id);
              }
            } else {
              const numRating = parseInt(rating, 10);
              if (!isNaN(numRating)) {
                handleSetPrimaryDrive(driveType, numRating);
              }
            }
          }}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="none">None</option>
          {driveOptions.map((option) => (
            <option key={option.rating} value={option.rating}>
              {option.label} (TL {option.techLevel})
            </option>
          ))}
        </select>
      </div>

      {/* Add Additional Drive */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Add Additional {getDriveTypeName()}
          </label>
          <span className="text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30 px-2 py-1 rounded">
            Adding additional drives does not automatically update power plant or fuel to account
          </span>
        </div>
        <select
          value=""
          onChange={(e) => {
            const rating = e.target.value;
            if (rating && rating !== "none") {
              const numRating = parseInt(rating, 10);
              if (!isNaN(numRating)) {
                dispatch(addDrive({ type: driveType, rating: numRating, role: 'additional' }));
              }
            }
            e.target.value = ""; // Reset dropdown after selection
          }}
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="">Select to add...</option>
          {driveOptions.map((option) => (
            <option key={option.rating} value={option.rating}>
              {option.label} (TL {option.techLevel})
            </option>
          ))}
        </select>
      </div>

      {/* Drive Cards */}
      <div className="space-y-3">
        {/* Primary Drive */}
        {primaryDrive && renderDriveCard(primaryDrive, 0)}
        
        {/* Additional Drives */}
        {additionalDrives.map((drive, index) => renderDriveCard(drive, index + 1))}
      </div>


      {/* Summary */}
      {drivesOfType.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <div className="flex justify-between">
              <span>Total {getDriveTypeName()}s:</span>
              <span className="font-medium">{drivesOfType.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Tonnage:</span>
              <span className="font-medium">
                {formatNumber(drivesOfType.reduce((sum, drive) => sum + calculateDriveTonnage(drive.rating), 0))} tons
              </span>
            </div>
            <div className="flex justify-between">
              <span>Total Cost:</span>
              <span className="font-medium">
                {formatCurrency(drivesOfType.reduce((sum, drive) => sum + calculateDriveCost(drive.rating), 0))}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
